/* rr-video.js
 *
 * Two jobs, for every video the theme renders.
 *
 * 1. TRIM. A video slot can carry data-rr-start and data-rr-end in seconds.
 *    The clip then plays only that window and loops inside it, which lets the
 *    owner point at one uploaded file and choose which few seconds of it the
 *    page actually shows, without re-cutting the file.
 *
 * 2. AUTOPLAY GUARANTEE. Browsers only permit unattended autoplay when a
 *    video is muted, inline, and has no audio track competing for focus. The
 *    markup already sets those attributes, but a video whose element is
 *    swapped, lazily inserted, or restored from the back/forward cache can
 *    arrive without them applied. This re-asserts them and retries play() once
 *    the browser is willing, so a slot never sits on a frozen first frame.
 *
 * Everything is idempotent: elements are marked once and re-scanning is safe,
 * which matters because the theme editor re-renders sections in place.
 */
(function () {
  'use strict';

  var MARK = 'rrVideoBound';

  function num(el, attr) {
    var v = parseFloat(el.getAttribute(attr));
    return isFinite(v) && v > 0 ? v : 0;
  }

  function bind(v) {
    if (!v || v.dataset[MARK]) return;
    v.dataset[MARK] = '1';

    /* Non-negotiable for unattended playback. Set as properties AND
       attributes, because Safari honours the attribute and Chrome the
       property, and a cloned node can lose one but not the other. */
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');

    var start = num(v, 'data-rr-start');
    var end = num(v, 'data-rr-end');
    /* An end at or before the start is meaningless; treat it as "no end". */
    if (end && end <= start) end = 0;
    var trimmed = start > 0 || end > 0;

    function seekToStart() {
      if (!start) return;
      try { if (Math.abs(v.currentTime - start) > 0.05) v.currentTime = start; } catch (e) {}
    }

    if (trimmed) {
      /* The theme loops its videos. With a trim, the native loop would restart
         at zero, so it is taken over here and the loop is done by hand. */
      v.loop = false;
      v.removeAttribute('loop');

      if (v.readyState >= 1) seekToStart();
      else v.addEventListener('loadedmetadata', seekToStart, { once: true });

      v.addEventListener('timeupdate', function () {
        var stop = end || v.duration;
        if (stop && v.currentTime >= stop - 0.03) {
          seekToStart();
          if (!start) { try { v.currentTime = 0; } catch (e) {} }
          play();
        }
      });
      /* If the file is shorter than the requested end, ended still fires. */
      v.addEventListener('ended', function () { seekToStart(); play(); });
    }

    function play() {
      var p = v.play();
      if (p && typeof p.catch === 'function') p.catch(function () { /* blocked, retried below */ });
    }

    play();
    /* A tab restored from the back/forward cache resumes paused. */
    v.addEventListener('canplay', play);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) play();
    });
  }

  function scan(root) {
    var list = (root || document).querySelectorAll('video');
    for (var i = 0; i < list.length; i++) bind(list[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }
  window.addEventListener('pageshow', function () { scan(); });

  /* The theme editor swaps section markup in place without a page load. */
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('shopify:section:select', function (e) { scan(e.target); });

  window.RRVideo = { scan: scan };
})();
